import api from '../api';

export interface CustomerDeliveryAddress {
  userAddressId: number;
  cityName: string;
  areaName: string;
  address1: string;
  address2: string | null;
  landmark: string | null;
  pincode?: string | null;
}

export interface CustomerDelivery {
  selectedAddressId: number | null;
  usesPosStock: boolean;
  pincode: string | null;
  requiresPincode: boolean;
  addresses: CustomerDeliveryAddress[];
  outOfStockProductIds: number[];
}

export const DELIVERY_CHANGED_KEY = 'customerDeliveryChanged';
const endpoint = () => {
  const tenant = process.env.REACT_APP_TENAT_ID;
  if (!tenant) throw new Error('The catalog is not configured.');
  return '/api/Home/CustomerDelivery/' + encodeURIComponent(tenant);
};

export async function getCustomerDelivery(): Promise<CustomerDelivery> {
  const { data } = await api.get<CustomerDelivery>(endpoint());
  if (!data || !Array.isArray(data.addresses) || !Array.isArray(data.outOfStockProductIds) ||
      typeof data.usesPosStock !== 'boolean' || typeof data.requiresPincode !== 'boolean' ||
      (data.pincode !== null && typeof data.pincode !== 'string') ||
      (data.selectedAddressId !== null && !data.addresses.some(a => a.userAddressId === data.selectedAddressId))) {
    throw new Error('Delivery availability could not be verified.');
  }
  return data; // Deliberately no shared/localStorage product or address cache.
}

export async function getPincodeDelivery(pincode: string): Promise<CustomerDelivery> {
  const pin = pincode.trim();
  if (!/^[1-9][0-9]{5}$/.test(pin)) throw new Error('Enter a valid six-digit pincode.');
  const { data } = await api.get<CustomerDelivery>(endpoint().replace('/CustomerDelivery/', '/PincodeDelivery/') + '/' + pin);
  if (!data || data.pincode !== pin || data.requiresPincode !== false || !Array.isArray(data.addresses) ||
      data.addresses.length !== 0 || data.selectedAddressId !== null || typeof data.usesPosStock !== 'boolean' ||
      !Array.isArray(data.outOfStockProductIds)) throw new Error('Pincode availability could not be verified.');
  return data;
}

export async function saveDefaultDeliveryAddress(userAddressId: number): Promise<void> {
  if (!Number.isSafeInteger(userAddressId) || userAddressId <= 0) throw new Error('Select a saved address.');
  const { data } = await api.put<{ isSuccess: boolean }>(endpoint() + '/default-address', { userAddressId });
  if (data?.isSuccess !== true) throw new Error('The delivery address was not saved.');
}

export function applyDeliveryAvailability<T extends { id: number; inStock: number }>(
  products: T[], delivery: CustomerDelivery | null, pending: boolean
): Array<T & { availabilityPending: boolean }> {
  const unavailable = new Set(delivery?.usesPosStock ? delivery.outOfStockProductIds : []);
  return products.map(product => ({ ...product,
    inStock: unavailable.has(product.id) ? 0 : product.inStock,
    availabilityPending: pending
  }));
}
